<!--
	Readout — the stat-pair primitive: a mono-caps label over a value set in the
	display face. Every proof point on the site is one of these.

	`countUp` animates the figure once, when it first scrolls into view. It parses the
	value rather than requiring a separate number prop, so callers keep passing the
	one authored string ("23,849 · one pass") and the component works out what part of
	it is a number. Anything it can't parse renders static — a readout that can't
	animate is normal, a readout that renders wrong is not.

	That principle is why the count refuses to start while the document is hidden.
	rAF is frozen in a background tab, so a count begun there sticks on its first
	frame: the readout would sit at "< 0.0 mm" where the truth is "< 0.3 mm", having
	replaced a correct server-rendered number with a wrong one. Every figure here is
	a measured claim about the product, so the rule is that the authored value is
	shown unless we can animate it properly, and the flourish is what gets dropped.

	Only use `countUp` on an actual quantity (faces, panels, licenses). A tolerance
	counting up from zero is nonsense — it doesn't accumulate, it bounds.
-->
<script lang="ts">
	import { motionDisabled } from '$lib/motion/reduced';

	type Size = 'sm' | 'md' | 'lg';

	let {
		label,
		value,
		size = 'lg',
		accent = false,
		countUp = false,
		class: klass = ''
	}: {
		label: string;
		value: string;
		size?: Size;
		/** Render the value in the accent — for live/measured data only. */
		accent?: boolean;
		/** Count the figure up when it enters view. */
		countUp?: boolean;
		class?: string;
	} = $props();

	/** Splits "≤ 23,849 faces" into prefix "≤ ", number 23849, suffix " faces". */
	const parsed = $derived.by(() => {
		const m = /^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/s.exec(value);
		if (!m) return null;
		const [, prefix, digits, suffix] = m;
		const n = Number(digits.replace(/,/g, ''));
		if (!Number.isFinite(n)) return null;
		const dot = digits.indexOf('.');
		return {
			prefix,
			suffix,
			target: n,
			decimals: dot === -1 ? 0 : digits.length - dot - 1,
			grouped: digits.includes(',')
		};
	});

	let el: HTMLParagraphElement;
	/** null = show the authored string verbatim (the SSR/static path). */
	let shown = $state<number | null>(null);

	const display = $derived.by(() => {
		if (shown === null || !parsed) return value;
		const n = shown.toLocaleString('en-US', {
			minimumFractionDigits: parsed.decimals,
			maximumFractionDigits: parsed.decimals,
			useGrouping: parsed.grouped
		});
		return `${parsed.prefix}${n}${parsed.suffix}`;
	});

	$effect(() => {
		if (!countUp || !parsed || motionDisabled()) return;
		const { target, decimals } = parsed;
		let raf = 0;
		const io = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;
				// Hidden tab: rAF won't run, so a count started now would freeze on its
				// first frame showing the wrong figure. Leave the authored value alone.
				if (document.hidden) return;
				io.disconnect();
				const dur = 900;
				const start = performance.now();
				const tick = (now: number) => {
					const t = Math.min((now - start) / dur, 1);
					// easeOutExpo — fast off the line, then settles onto the number.
					const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
					const v = target * eased;
					// Snap to the value's own precision so it doesn't land on 23,848.97.
					shown = t === 1 ? target : Number(v.toFixed(decimals));
					if (t < 1) raf = requestAnimationFrame(tick);
				};
				raf = requestAnimationFrame(tick);
			},
			{ threshold: 0.6 }
		);
		io.observe(el);
		return () => {
			io.disconnect();
			cancelAnimationFrame(raf);
		};
	});
</script>

<div class="ro {klass}">
	<p class="ro__label">{label}</p>
	<p bind:this={el} class="ro__value" data-size={size} class:ro__value--accent={accent}>
		{display}
	</p>
</div>

<style>
	.ro__label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.ro__value {
		margin-top: 0.375rem;
		font-family: var(--font-display);
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--color-ink-900);
		font-variant-numeric: tabular-nums;
		text-wrap: balance;
	}
	.ro__value[data-size='lg'] {
		font-size: clamp(1.75rem, 2.6vw, 2.5rem);
		line-height: 1.05;
	}
	.ro__value[data-size='md'] {
		font-size: var(--text-h3);
		line-height: 1.2;
	}
	.ro__value[data-size='sm'] {
		font-size: 1rem;
		line-height: 1.3;
	}
	.ro__value--accent {
		color: var(--color-cut-700);
	}

	:global(.sec--dark) .ro__label,
	:global(.app-surface) .ro__label {
		color: var(--color-bone-500);
	}
	:global(.sec--dark) .ro__value,
	:global(.app-surface) .ro__value {
		color: var(--color-bone-100);
	}
	:global(.sec--dark) .ro__value--accent,
	:global(.app-surface) .ro__value--accent {
		color: var(--color-cut-400);
	}
</style>
